import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ErrorBox } from '../ui/kit';

const FIELDS = ['client_id', 'redirect_uri', 'code_challenge', 'code_challenge_method', 'state', 'mode'] as const;

/** F26: the Gmail extension's sign-in lands here (from /oauth/ext/authorize). The person confirms, the app mints a
 *  one-time code and sends the browser back to the extension, which trades it for its own short-lived key. */
export function ConnectGmail() {
  const { me } = useAuth();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  useEffect(() => { document.title = 'Outlevo · Connect Gmail'; }, []);
  const q = Object.fromEntries(FIELDS.map((k) => [k, params.get(k) ?? ''])) as Record<(typeof FIELDS)[number], string>;
  const complete = FIELDS.every((k) => k === 'mode' || q[k]);
  const extensionReturn = /^https:\/\/[a-p]{32}\.chromiumapp\.org\//.test(q.redirect_uri);

  const allow = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ redirect_to: string }>('/portal/ext/authorize', { method: 'POST', body: { ...q, mode: q.mode || 'live' } });
      location.href = r.redirect_to;
    } catch (e) { setErr(e as ApiError); setBusy(false); }
  };
  const cancel = () => {
    if (!extensionReturn) { location.href = '/'; return; }
    const to = new URL(q.redirect_uri);
    to.searchParams.set('error', 'access_denied'); to.searchParams.set('state', q.state);
    location.href = to.toString();
  };

  return (
    <div className="auth connect-card">
      <h1>Outlevo for Gmail</h1>
      {!complete || !extensionReturn ? <>
        <p>This link is incomplete. Open the Outlevo sidebar in Gmail and press <strong>Sign in to Outlevo</strong> again.</p>
        <a className="btn" href="/">Go to Outlevo</a>
      </> : <>
        <p>Allow <strong>Outlevo for Gmail</strong> to act as you in <strong>{me?.org.name ?? 'your workspace'}</strong>{q.mode === 'test' ? ' (test mode)' : ''}?</p>
        <ul className="small">
          <li>It shows what Outlevo knows about the people in the email you have open, and warns before you write to someone who must not be contacted.</li>
          <li>It reads nothing until you open its sidebar, and sends an email's text only when you ask it to draft a reply.</li>
          <li>It signs in for 12 hours at a time. You can sign it out under Developers → API keys.</li>
        </ul>
        {err ? <ErrorBox error={err} doing="Connecting the Gmail extension" /> : null}
        <span className="actions">
          <button className="primary" onClick={allow} disabled={busy}>{busy ? 'Connecting…' : 'Allow'}</button>
          <button onClick={cancel} disabled={busy}>Cancel</button>
        </span>
      </>}
    </div>
  );
}
